import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import { ApiDataResponse, Dashboard } from "../services/Dtos";
import { useUserContext } from "./userContext";
import useAxios from "../hooks/useAxios";
import { useGlobalError } from "./GlobalErrorContext";
import dashboardService from "../services/dashboardService";

interface DashboardsContextType {
  dashboard: Dashboard | null;
  setDashboard: React.Dispatch<React.SetStateAction<Dashboard | null>>;
  error: string | null;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  connectionState: string;
}

interface Props {
  children: ReactNode;
}

const DashboardContext = createContext<DashboardsContextType | undefined>(
  undefined
);

export const DashboardsProvider = ({ children }: Props) => {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionState, setConnectionState] =
    useState<string>("Disconnected");
  const { user, isLoading: userLoading } = useUserContext();
  const axiosClient = useAxios();
  const { setError: setGlobalError } = useGlobalError();

  // Setup SignalR dashboard service
  useEffect(() => {
    if (!user?.schoolId || !user?.id) return;

    // Set up dashboard data update handler
    dashboardService.onDashboardDataUpdate((data: Dashboard) => {
      setDashboard(data);
    });

    // Set up connection state change handler
    dashboardService.onConnectionStateChanged((state: string) => {
      setConnectionState(state);
    });

    // Connect to SignalR hub
    const connectToHub = async () => {
      try {
        await dashboardService.connect(user.schoolId, user.id);
      } catch (error) {
      }
    };

    connectToHub();

    // Cleanup on unmount
    return () => {
      dashboardService.disconnect();
    };
  }, [user?.schoolId, user?.id]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!user || !token || userLoading) {
      setIsLoading(false);
      return;
    }
    let isCancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      if (!user || !localStorage.getItem("token") || isCancelled) {
        setIsLoading(false);
        return;
      }
      try {
        setError(null);
        // Fetch dashboards
        const dashboardsResponse = await axiosClient.get<
          ApiDataResponse<Dashboard>
        >("dashboarddata", { headers: { SchoolId: user?.schoolId } });
        if (isCancelled) return;
        if (dashboardsResponse.data?.succeeded) {
          setDashboard(dashboardsResponse.data.data);
          // Removed fetchStudents() call - StudentsContext handles this
        } else {
          setError(dashboardsResponse.data?.message);
          setGlobalError({
            message:
              dashboardsResponse.data?.message ||
              "Failed to fetch dashboard data.",
            onRetry: () => window.location.reload(),
          });
        }
      } catch (error) {
        if (isCancelled) return;
        const errorMessage = "Failed to fetch dashboard data";
        setError(errorMessage);
      } finally {
        if (isCancelled) return;
        setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [user, userLoading]);

  return (
    <DashboardContext.Provider
      value={{
        dashboard,
        setDashboard,
        error,
        isLoading,
        setIsLoading,
        connectionState,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error(
      "useDashboardContext must be used within a DashboardProvider"
    );
  }
  return context;
};
